import Vue from 'vue';
import Vuex from 'vuex';
import { PERMISSION_PREFIX, permissionsForDrive, hasPermission, canBrowseDrive } from '@/plugins/permissions';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: null,
    permissions: [],
    permissionPrefix: PERMISSION_PREFIX,
    buckets: ['gc-game-translations', 'degen-translations', 'marbles-rounds-replay'],
    bucket: 'gc-game-translations',
    translationServerEnabled: false,
    compareServerEnabled: false,
    localeTranslationRuleLocales: [],
    keepEnglishFeatures: [],
    translationConfig: null,
    selectedTranslationModel: null,
    aiTranslationOverlay: {
      visible: false,
      message: 'Translating…',
      detail: ''
    }
  },
  getters: {
    permissionNames(state) {
      return permissionsForDrive(state.permissionPrefix, state.bucket);
    },
    visibleBuckets(state) {
      return (state.buckets || []).filter((drive) => (
        canBrowseDrive(state.permissions, state.permissionPrefix, drive)
      ));
    },
    hasPermission: (state) => (permission) => hasPermission(state.permissions, permission),
    canCreate(state, getters) {
      return getters.hasPermission(getters.permissionNames.FILES_CREATE);
    },
    canUpdate(state, getters) {
      return getters.hasPermission(getters.permissionNames.FILES_UPDATE);
    },
    canDelete(state, getters) {
      return getters.hasPermission(getters.permissionNames.FILES_DELETE);
    },
    canUseAi(state, getters) {
      return getters.hasPermission(getters.permissionNames.TRANSLATION_AI);
    },
    isTranslationEnabled(state, getters) {
      return state.translationServerEnabled && getters.canUseAi;
    },
    isCompareTranslationEnabled(state, getters) {
      return state.compareServerEnabled && getters.canUseAi;
    },
    translationConfig(state) {
      return state.translationConfig;
    },
    translationModel(state, getters) {
      return getters.selectedTranslationModel ?? 'off';
    },
    translationModelLabel(state, getters) {
      return (
        getters.selectedTranslationModelConfig?.modelLabel ??
        state.translationConfig?.modelLabel ??
        state.translationConfig?.model ??
        'AI'
      );
    },
    translationModels(state) {
      return state.translationConfig?.models || [];
    },
    showTranslationModelPicker(state, getters) {
      return getters.translationModels.length > 1;
    },
    selectedTranslationModel(state, getters) {
      if (
        state.selectedTranslationModel &&
        getters.translationModels.some(entry => entry.model === state.selectedTranslationModel)
      ) {
        return state.selectedTranslationModel;
      }
      return (
        state.translationConfig?.defaultModel ??
        state.translationConfig?.model ??
        'off'
      );
    },
    selectedTranslationModelConfig(state, getters) {
      const modelId = getters.selectedTranslationModel;
      return (
        getters.translationModels.find(entry => entry.model === modelId) ||
        state.translationConfig
      );
    }
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
      if (!user) {
        state.permissions = [];
        state.permissionPrefix = PERMISSION_PREFIX;
      }
    },
    setPermissions(state, permissions) {
      state.permissions = Array.isArray(permissions) ? permissions : [];
    },
    setPermissionPrefix(state, prefix) {
      const value = String(prefix || '').trim();
      if (value) {
        state.permissionPrefix = value;
      }
    },
    setBucket(state, bucket) {
      state.bucket = bucket;
    },
    setBuckets(state, buckets) {
      const list = Array.isArray(buckets) ? buckets.filter(Boolean) : [];
      state.buckets = list;
      if (!list.includes(state.bucket)) {
        state.bucket = list[0] || '';
      }
    },
    setTranslationServerEnabled(state, enabled) {
      state.translationServerEnabled = Boolean(enabled);
    },
    setCompareServerEnabled(state, enabled) {
      state.compareServerEnabled = Boolean(enabled);
    },
    setLocaleTranslationRuleLocales(state, locales) {
      state.localeTranslationRuleLocales = Array.isArray(locales) ? locales : [];
    },
    setKeepEnglishFeatures(state, terms) {
      state.keepEnglishFeatures = Array.isArray(terms) ? terms : [];
    },
    setTranslationConfig(state, config) {
      if (!config || !config.model || String(config.model).toLowerCase() === 'off') {
        state.translationConfig = null;
        state.selectedTranslationModel = null;
        return;
      }
      state.translationConfig = config;

      const models = config.models || [];
      let stored = null;
      try {
        stored = sessionStorage.getItem('translation-selected-model');
      } catch {
        stored = null;
      }

      if (stored && models.some(entry => entry.model === stored)) {
        state.selectedTranslationModel = stored;
        return;
      }

      state.selectedTranslationModel = config.defaultModel || config.model;
    },
    setSelectedTranslationModel(state, model) {
      const value = String(model || '').trim();
      if (!value) return;
      state.selectedTranslationModel = value;
      try {
        sessionStorage.setItem('translation-selected-model', value);
      } catch {
        /* ignore */
      }
    },
    setAiTranslationOverlay(state, payload = {}) {
      state.aiTranslationOverlay = {
        visible: Boolean(payload.visible),
        message: payload.message || 'Translating…',
        detail: payload.detail || ''
      };
    }
  }
});
